import {
  ComponentRenderData, extractPlasmicQueryData, PlasmicComponent, PlasmicRootProvider
} from "@plasmicapp/loader-nextjs";
import type { GetStaticProps } from "next";
import * as React from "react";

import craft from "@pages/api/craft";
import QueryHeaderFooter from "@queries/QueryHeaderFooter.graphql";
import QueryResourcesIndex from "@queries/resources/QueryResourcesIndex.graphql";

import { PLASMIC } from "@abnormal/plasmic-init";
import Error from "next/error";
import Head from "next/head";
import { useRouter } from "next/router";
import { Seomatic } from "react-seomatic";

import getGlobals from "@lib/getGlobals";

export default function Page(props: {
  plasmicData?: ComponentRenderData;
  queryCache?: Record<string, any>;
  pageEntry?: {seomatic: Record<string, string>};
}) {
  const { plasmicData, queryCache, pageEntry } = props;
  const router = useRouter();
  if (!plasmicData || plasmicData.entryCompMetas.length === 0) {
    return <Error statusCode={404} />;
  }
  const pageMeta = plasmicData.entryCompMetas[0];

  return (
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      prefetchedQueryData={queryCache}
      pageParams={pageMeta.params}
      pageQuery={router.query}
    >
      <Seomatic Head={Head} {...pageEntry.seomatic} />
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  const plasmicPath = "/resources"

  const plasmicData = await PLASMIC.maybeFetchComponentData([
    plasmicPath,
    "Footer"
  ], {
    // deferChunks: true,
  });

  if (!plasmicData) {
    // non-Plasmic catch-all
    return { props: {} };
  }

  const response = await craft(QueryHeaderFooter);

  const {
    primaryNavigation,
    primaryCallToActions,
    persistentNav,
    globalSets,
  } = response;

  const IndexResponse = await craft(QueryResourcesIndex, { limit: 6 })

  const {
    pageEntry,
  } = IndexResponse;

  const globals = await getGlobals(globalSets);

  const pageMeta = plasmicData.entryCompMetas[0];
  // Cache the necessary data fetched for the page
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      pageParams={pageMeta.params}
    >
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>
  );

  // Use revalidate if you want incremental static regeneration
  return {
    props: {
      plasmicData,
      queryCache,
      navigation: {
        primaryNavigation,
        primaryCallToActions,
      },
      persistentNav,
      globals,
      headerTheme: "dark",
      pageEntry,
    },
    revalidate: 60,
  };
};
